import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageShell from "@/components/PageShell";
import api from "@/lib/api";
import { Route, ArrowRight, Check, Circle, PlayCircle } from "lucide-react";
import { ReadAloud } from "@/components/ReadAloud";
import { TrackAudioPlayer } from "@/components/TrackAudioPlayer";
import { motion } from "framer-motion";

export default function StudyPaths() {
  const [tracks, setTracks] = useState([]);
  const [completed, setCompleted] = useState([]);

  useEffect(() => {
    api.get("/tracks").then((r) => setTracks(r.data));
    api.get("/progress").then((r) => setCompleted(r.data.lessons || [])).catch(() => {});
  }, []);

  const nextCourse = () => {
    for (const t of tracks) {
      for (const c of t.courses) {
        if (!completed.some((k) => k.startsWith(`${c.slug}:`))) return c;
      }
    }
    return null;
  };
  const recommended = nextCourse();

  return (
    <PageShell>
      <div className="max-w-7xl mx-auto px-6 py-14">
        <div className="flex items-center gap-3 mb-2">
          <Route className="w-7 h-7 text-primary" />
          <h1 className="font-serif text-5xl">Guided Study Path</h1>
          <ReadAloud className="ml-auto" label="Autoplay intros"
            getText={() => `Guided Study Path. ${tracks.map((t, ti) => `Track ${ti + 1}: ${t.name}. ${t.blurb} ${t.courses.map((c, ci) => `Course ${ci + 1}: ${c.title}. ${c.subtitle || ""} ${c.summary || ""}`).join(" ")}`).join(" ")}`} />
        </div>
        <p className="text-muted-foreground max-w-2xl mb-10">
          Three tracks, in the order a beginner should take them. Finish one course to unlock the next — you'll always
          know exactly where to go.
        </p>

        {recommended && (
          <Link to={`/courses/${recommended.slug}`} data-testid="continue-recommended"
            className="block bg-primary text-primary-foreground p-8 mb-12 group">
            <span className="text-xs uppercase tracking-widest font-mono opacity-80">Recommended next</span>
            <div className="flex items-center justify-between mt-2">
              <h2 className="font-serif text-3xl">{recommended.title}</h2>
              <PlayCircle className="w-10 h-10 group-hover:scale-110 transition-transform" />
            </div>
          </Link>
        )}

        <div className="space-y-12">
          {tracks.map((t, ti) => (
            <div key={t.id}>
              <div className="flex items-center gap-4 mb-1 flex-wrap">
                <span className="font-mono text-primary text-sm">0{ti + 1}</span>
                <h2 className="font-serif text-3xl">{t.name}</h2>
                <TrackAudioPlayer track={t} className="ml-auto" />
              </div>
              <p className="text-sm text-muted-foreground mb-6 ml-9">{t.blurb}</p>

              <div className="ml-9 border-l border-border pl-6 space-y-3">
                {t.courses.map((c, ci) => {
                  const done = completed.some((k) => k.startsWith(`${c.slug}:`));
                  return (
                    <motion.div key={c.slug} initial={{ opacity: 0, x: -8 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: ci * 0.05 }}>
                      <Link to={`/courses/${c.slug}`} data-testid={`path-course-${c.slug}`}
                        className="flex items-center gap-4 bg-card border border-border p-4 hover:border-primary transition-colors group">
                        {done ? <Check className="w-5 h-5 text-primary shrink-0" /> : <Circle className="w-5 h-5 text-muted-foreground shrink-0" />}
                        <div className="flex-1">
                          <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">{c.level} · {c.category}</div>
                          <div className="font-serif text-xl leading-tight group-hover:text-primary transition-colors">{c.title}</div>
                        </div>
                        <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </PageShell>
  );
}
